import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { UsersIcon, SearchIcon, CalendarIcon, MapPinIcon, ChevronDownIcon, ChevronUpIcon, SparklesIcon, BookOpenIcon, ClockIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import BackButton from '../../components/UI/BackButton';
import SaveEventButton from '../../components/Events/SaveEventButton';
import { mockEventDetails } from './data';
import { EventHeader } from './EventHeader';
import { LocationBlock } from './LocationBlock';
import { AccessInfoBlock } from './AccessInfoBlock';
import { ActionButtons } from './ActionButtons';
const SCHEDULE_TYPE_STYLES: Record<string, string> = {
  signing: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  panel: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  workshop: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  social: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300',
  break: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
};
const EventDetail = () => {
  const {
    id
  } = useParams();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [isSaved, setIsSaved] = useState(false);
  const [authorSearch, setAuthorSearch] = useState('');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  // Find event or default to first mock event for demo purposes
  const event = mockEventDetails.find(e => e.id === id) || mockEventDetails[0];
  const filteredAuthors = event?.attendingAuthors?.filter(author => authorSearch === '' || author.name.toLowerCase().includes(authorSearch.toLowerCase()) || author.genres.some(g => g.toLowerCase().includes(authorSearch.toLowerCase()))) || [];
  const toggleFaq = (index: number) => {
    setExpandedFaq(prev => prev === index ? null : index);
  };
  if (!event) {
    return <div className={`min-h-screen pt-24 pb-12 px-4 text-center ${dk.page} ${dk.textPrimary}`} data-id="element-9676" data-mp="20f3tj55gerwg">
        <h1 className="text-2xl font-bold mb-4" data-id="element-9677" data-mp="24ggyze4mwt2u">Event not found</h1>
        <Link to="/events" className="text-purple-500 hover:underline" data-id="element-9678" data-mp="29e8sgckxgn95">
          Return to Events
        </Link>
      </div>;
  }
  return <div className={`min-h-screen pb-12 ${dk.page}`} data-id="element-9679" data-mp="1gpbm97tzqgwr">
      {/* Banner Image */}
      {event.bannerImage ? <div className="relative w-full h-64 md:h-80 lg:h-96" data-id="element-9680" data-mp="1lnljrp36ttlj">
          <img src={event.bannerImage} alt={event.eventName} className="w-full h-full object-cover" data-id="element-9681" data-mp="1llsas353qxad" />
          <div className={`absolute inset-0 bg-gradient-to-t ${dk.darkMode ? 'from-gray-900 via-gray-900/60' : 'from-gray-50 via-gray-50/60'} to-transparent`} data-id="element-9682" data-mp="1vezp4dhtlnml" />
          <div className="absolute top-24 left-4 sm:left-8 z-10" data-id="element-9683" data-mp="12x3rflm9p00p">
            <BackButton data-id="element-9684" data-mp="12vajm2zx49r4" />
          </div>
          <div className="absolute top-24 right-4 sm:right-8 z-10" data-id="element-9685" data-mp="vee3vk6j89uy">
            <div className="relative w-10 h-10" data-id="element-9686" data-mp="qf1f2tw3ospq">
              <SaveEventButton eventId={parseInt(event.id)} isSaved={isSaved} onToggleSave={() => setIsSaved(!isSaved)} data-id="element-9687" data-mp="qgci2rhq1wfs" />
            </div>
          </div>
        </div> : <div className="pt-24 px-4 sm:px-8 mb-6 flex justify-between items-center max-w-7xl mx-auto" data-id="element-9688" data-mp="gj7sehoo3iu8">
          <BackButton data-id="element-9689" data-mp="7ojr12m7qlap" />
          <div className="relative w-10 h-10" data-id="element-9690" data-mp="7o06rf93bkti">
            <SaveEventButton eventId={parseInt(event.id)} isSaved={isSaved} onToggleSave={() => setIsSaved(!isSaved)} data-id="element-9691" data-mp="7n8of64i7jl6" />
          </div>
        </div>}

      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${event.bannerImage ? '-mt-20 relative z-10' : ''}`} data-id="element-9692" data-mp="3ttnf8la83woi">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-9693" data-mp="3kvbmaclvwmsw">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2" data-id="element-9694" data-mp="dg1z9giv0ee1">
            <EventHeader event={event} data-id="element-9695" data-mp="dh3m0i98mpbl" />

            {/* Highlights */}
            {event.highlights && event.highlights.length > 0 && <div className="flex flex-wrap gap-2 mb-8" data-id="element-9696" data-mp="4o9qrbdebfc3">
                {event.highlights.map((highlight, idx) => <span key={idx} className={`inline-flex items-center px-3 py-1.5 ${rt.badgeRadius} text-sm font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300`} data-id="element-9697" data-mp="8otsks8wz74k" data-mpi={idx}>
                    <SparklesIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-9698" data-mp="3vryhj044rmhi" />
                    {highlight}
                  </span>)}
              </div>}

            {/* Description */}
            <div className={`prose max-w-none mb-10 ${dk.darkMode ? 'prose-invert' : ''}`} data-id="element-9699" data-mp="3lxydl1kl4nad">
              {(event.longDescription || event.shortDescription).split('\n\n').map((paragraph, idx) => <p key={idx} className={`text-lg leading-relaxed mb-4 ${dk.textSecondary}`} data-id="element-9700" data-mp="34ncd4iy3u2gc" data-mpi={idx}>
                    {paragraph}
                  </p>)}
            </div>

            {/* Attending Authors */}
            {event.attendingAuthors && event.attendingAuthors.length > 0 && <section className={`p-6 rounded-xl shadow-sm border mb-6 ${dk.card} ${dk.border}`} data-id="element-9701" data-mp="3oaactpbnkn41">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-id="element-9702" data-mp="3jaxh0f2t05pb">
                  <h2 className={`text-lg font-bold flex items-center ${dk.textHeading}`} data-id="element-9703" data-mp="15kfanxaeoa8">
                    <UsersIcon className="w-5 h-5 mr-2 text-purple-500" data-id="element-9704" data-mp="3sakjl479rhjy" />
                    Attending Authors
                    <span className={`ml-2 text-sm font-normal ${dk.textMuted}`} data-id="element-9705" data-mp="1xydxvbgr1ide">
                      ({event.attendingAuthors.length})
                    </span>
                  </h2>
                  <div className="relative w-full sm:w-64" data-id="element-9706" data-mp="6tj3yd2u6es5">
                    <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${dk.textMuted}`} data-id="element-9707" data-mp="6w5hqugblqit" />
                    <input type="text" placeholder="Search authors or genres..." value={authorSearch} onChange={e => setAuthorSearch(e.target.value)} className={`w-full pl-9 pr-4 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${dk.darkMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`} data-id="element-9708" data-mp="fvs9spn5tjz7" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-id="element-9709" data-mp="18kprornglerf">
                  {filteredAuthors.map(author => <div key={author.id} className={`rounded-xl border p-4 transition-shadow hover:shadow-md ${dk.card} ${dk.border}`} data-id="element-9710" data-mp="2szmb7jrod9pd">
                      <div className="flex items-start gap-3 mb-3" data-id="element-9711" data-mp="2k2vgbaov00wd">
                        <img src={author.image} alt={author.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" data-id="element-9712" data-mp="2f3ii5tsjf7eh" />
                        <div className="min-w-0" data-id="element-9713" data-mp="37sz88ksoqc7j">
                          <h3 className={`font-bold text-sm truncate ${dk.textPrimary}`} data-id="element-9714" data-mp="32yv0w0fic25x">
                            {author.name}
                          </h3>
                          <div className="flex flex-wrap gap-1 mt-1" data-id="element-9715" data-mp="3vhqqokgh3g74">
                            {author.genres.map(genre => <span key={genre} className={`text-[10px] px-1.5 py-0.5 rounded-full ${dk.darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`} data-id="element-9716" data-mp="3qnmn8b6f1qas">
                                {genre}
                              </span>)}
                          </div>
                        </div>
                      </div>

                      {(author.tableNumber || author.signingSlot) && <div className={`flex flex-wrap gap-x-4 gap-y-1 text-xs mb-3 ${dk.textMuted}`} data-id="element-9717" data-mp="3mqfj0qplsue9">
                          {author.tableNumber && <span className="flex items-center" data-id="element-9718" data-mp="3crzqqkeubf36">
                              <MapPinIcon className="h-3 w-3 mr-1" data-id="element-9719" data-mp="1xcshoty13yxy" />
                              {author.tableNumber}
                            </span>}
                          {author.signingSlot && <span className="flex items-center" data-id="element-9720" data-mp="1ttbkdne4koj0">
                              <ClockIcon className="h-3 w-3 mr-1" data-id="element-9721" data-mp="1ovs4kj1s43oy" />
                              {author.signingSlot}
                            </span>}
                        </div>}

                      <div className="flex gap-2 mb-3" data-id="element-9722" data-mp="1yv0yxd0k3kbu">
                        {author.books.map(book => <div key={book.title} className="flex-1 min-w-0" data-id="element-9723" data-mp="1b4n115vair9c">
                            <img src={book.coverImage} alt={book.title} className="w-full h-20 object-cover rounded-md" data-id="element-9724" data-mp="1663f35gp5fgz" />
                            <p className={`text-[10px] mt-1 truncate ${dk.textMuted}`} data-id="element-9725" data-mp="1g155s5qmeplo">
                              {book.title}
                            </p>
                          </div>)}
                      </div>

                      <Link to={author.profileLink} className={`block text-center text-xs font-medium py-1.5 rounded-md transition-colors ${dk.darkMode ? 'bg-gray-700 text-indigo-300 hover:bg-gray-600' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}`} data-id="element-9726" data-mp="3i92718y0omg">
                        View Profile
                      </Link>
                    </div>)}
                </div>

                {filteredAuthors.length === 0 && <div className="text-center py-8" data-id="element-9727" data-mp="37yk0is2u834d">
                    <p className={`text-sm ${dk.textMuted}`} data-id="element-9728" data-mp="37zbi2yzlo0bn">
                      No authors match your search.
                    </p>
                  </div>}
              </section>}

            {/* Schedule */}
            {event.schedule && event.schedule.length > 0 && <section className={`p-6 rounded-xl shadow-sm border mb-6 ${dk.card} ${dk.border}`} data-id="element-9729" data-mp="2t2b19qiy5pry">
                <h2 className={`text-lg font-bold mb-6 flex items-center ${dk.textHeading}`} data-id="element-9730" data-mp="2nwp3txzivv7q">
                  <CalendarIcon className="w-5 h-5 mr-2 text-purple-500" data-id="element-9731" data-mp="2svigxpawdk7d" />
                  Event Schedule
                </h2>

                <div className="space-y-4" data-id="element-9732" data-mp="2xud3n2bzjswr">
                  {event.schedule.map(item => <div key={item.id} className={`flex gap-4 p-4 rounded-lg border ${dk.darkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-100 bg-gray-50/50'}`} data-id="element-9733" data-mp="208o2pa4obh2c">
                      {/* Time column */}
                      <div className="flex-shrink-0 w-24 text-right" data-id="element-9734" data-mp="2ex2c63wli3rv">
                        <p className={`text-sm font-bold ${dk.textPrimary}`} data-id="element-9735" data-mp="1hgvbnl9605x9">
                          {item.time}
                        </p>
                        <p className={`text-xs ${dk.textMuted}`} data-id="element-9736" data-mp="1me55dd3l73qw">
                          {item.endTime}
                        </p>
                      </div>

                      {/* Vertical line */}
                      <div className="flex flex-col items-center" data-id="element-9737" data-mp="19fnkbk0nbfeo">
                        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${SCHEDULE_TYPE_STYLES[item.type]?.split(' ')[0] || 'bg-gray-200'}`} data-id="element-9738" data-mp="1o3s5qvr32515" />
                        <div className={`w-0.5 flex-1 mt-1 ${dk.darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} data-id="element-9739" data-mp="vfcvsnmt6cwo" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0 pb-2" data-id="element-9740" data-mp="15fwx1qabvhew">
                        <div className="flex flex-wrap items-center gap-2 mb-1" data-id="element-9741" data-mp="10epqfujx8o9t">
                          <h3 className={`font-bold text-sm ${dk.textPrimary}`} data-id="element-9742" data-mp="2gt8unjfhv3sm">
                            {item.title}
                          </h3>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium uppercase tracking-wider ${SCHEDULE_TYPE_STYLES[item.type]}`} data-id="element-9743" data-mp="2btw7exkpnupi">
                            {item.type}
                          </span>
                        </div>
                        <p className={`text-sm mb-2 ${dk.textSecondary}`} data-id="element-9744" data-mp="2af1zy7t6fkt3">
                          {item.description}
                        </p>
                        <div className={`flex flex-wrap gap-x-4 gap-y-1 text-xs ${dk.textMuted}`} data-id="element-9745" data-mp="323wqjth49lth">
                          {item.location && <span className="flex items-center" data-id="element-9746" data-mp="2tcmo1co4afcn">
                              <MapPinIcon className="h-3 w-3 mr-1" data-id="element-9747" data-mp="2taa62zid04zl" />
                              {item.location}
                            </span>}
                          {item.speakers && item.speakers.length > 0 && <span className="flex items-center" data-id="element-9748" data-mp="3pv0yqsgwm4go">
                              <UsersIcon className="h-3 w-3 mr-1" data-id="element-9749" data-mp="225ece4vtty04" />
                              {item.speakers.join(', ')}
                            </span>}
                        </div>
                      </div>
                    </div>)}
                </div>
              </section>}

            <LocationBlock event={event} data-id="element-9750" data-mp="1o7hbwxe957g6" />
            <AccessInfoBlock event={event} data-id="element-9751" data-mp="2kw5qtkuo8eld" />

            {/* FAQ */}
            {event.faqItems && event.faqItems.length > 0 && <section className={`p-6 rounded-xl shadow-sm border mb-6 ${dk.card} ${dk.border}`} data-id="element-9752" data-mp="2fwswyg5du79a">
                <h2 className={`text-lg font-bold mb-4 flex items-center ${dk.textHeading}`} data-id="element-9753" data-mp="2b4j8z5ntvh9n">
                  <BookOpenIcon className="w-5 h-5 mr-2 text-purple-500" data-id="element-9754" data-mp="33mn54kerb7n7" />
                  Frequently Asked Questions
                </h2>

                <div className="space-y-2" data-id="element-9755" data-mp="2yuvnx0hhqe9q">
                  {event.faqItems.map((faq, idx) => <div key={idx} className={`rounded-lg border overflow-hidden ${dk.border}`} data-id="element-9756" data-mp="lec4avllwxda" data-mpi={idx}>
                      <button onClick={() => toggleFaq(idx)} className={`w-full flex items-center justify-between p-4 text-left transition-colors ${dk.darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`} data-id="element-9757" data-mp="ue8n4glbf20f">
                        <span className={`font-medium text-sm pr-4 ${dk.textPrimary}`} data-id="element-9758" data-mp="14ac06hhpgl25">
                          {faq.question}
                        </span>
                        {expandedFaq === idx ? <ChevronUpIcon className={`h-4 w-4 flex-shrink-0 ${dk.textMuted}`} data-id="element-9759" data-mp="1i29rddbgnwa1" /> : <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 ${dk.textMuted}`} data-id="element-9760" data-mp="1s0xxbdq68kxd" />}
                      </button>
                      {expandedFaq === idx && <div className={`px-4 pb-4 ${dk.textSecondary}`} data-id="element-9761" data-mp="3j3y3yr5cbkbd">
                          <p className="text-sm leading-relaxed" data-id="element-9762" data-mp="1qvowlj7bjjd">
                            {faq.answer}
                          </p>
                        </div>}
                    </div>)}
                </div>
              </section>}
          </div>

          {/* Right Column - Sidebar */}
          <div className="lg:col-span-1" data-id="element-9763" data-mp="knwvgaselrpf">
            <ActionButtons event={event} data-id="element-9764" data-mp="kmu461r65n8z" />
          </div>
        </div>
      </div>
    </div>;
};
export default EventDetail;